import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { fetchData, updateData, postData, SyncData } from '../../../apiService';
import {
  Card,
  CardContent,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import RestaurantMenu from '@mui/icons-material/Restaurant';
import DescriptionModal from './DescriptionModal'; // Import the DescriptionModal component
import ReactDOM from 'react-dom';
import EstimatePrint from '../KOT/Estimate';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';

const CreateKOT = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, userId, token, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
    }
  }, [isAuthenticated, companyId, token, logout]);

  const [expandedCategories, setExpandedCategories] = useState([]);
  const handleExpandClick = (categoryName) => {
    setExpandedCategories((prevExpanded) => {
      if (prevExpanded.includes(categoryName)) {
        return prevExpanded.filter((cat) => cat !== categoryName);
      } else {
        return [...prevExpanded, categoryName];
      }
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [description, setDescription] = useState('');

  const saveDescription = (newDescription) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.ItemId === selectedItemId
          ? { ...item, description: newDescription }
          : item
      )
    );

    setIsModalOpen(false);
  };

  const [totalQuantity, setTotalQuantity] = useState(0);
  const [kotDate, setKOTDate] = useState('');
  const [kotTime, setKOTTime] = useState('');
  const [tableName, setTableName] = useState('');
  const [tableId, setTableId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const currentDate = new Date();
  const formattedDate = currentDate.toISOString().split('T')[0];
  const formattedTime = currentDate.toTimeString().split(' ')[0];

  const [categoryData, setCategoryData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    fetchData('KOTApi', `/ItemTableDetails/${companyId}`, token)
      .then((response) => {
        if (response.responseCode === '1111') {
          setCategoryData(response.response_data);
        }
      })
      .catch((error) => {
        console.error('Error fetching Category Data:', error);
      });

    fetchData('TableNameApi', `/TableNameDetails/${id}`, token)
      .then((response) => {
        if (response.responseCode === '1111') {
          setTableId(response.response_data.Id);
          setTableName(response.response_data.Name);
          setKOTDate(formattedDate);
          setKOTTime(formattedTime);
        }
      })
      .catch((error) => {
        console.error('Error fetching Table Name Details:', error);
      });
  }, [id, formattedDate, formattedTime]);

  const handleIncrement = (CategoryId, ItemId, itemName) => {
    setSelectedItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.ItemId === ItemId);
      if (existingItem) {
        // If the item is already in the list, update its quantity
        setTotalQuantity((prevTotal) => prevTotal + 1);
        return prevItems.map((item) =>
          item.ItemId === ItemId ? { ...item, Quantity: item.Quantity + 1 } : item
        );
      } else {
        // If the item is not in the list, add it with quantity 1
        setTotalQuantity((prevTotal) => prevTotal + 1);
        return [...prevItems, { CategoryId, ItemId, Quantity: 1, itemName }];
      }
    });
  };

  const handleDecrement = (ItemId) => {
    setSelectedItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.ItemId === ItemId);
      if (existingItem) {
        setTotalQuantity((prevTotal) => Math.max(prevTotal - 1, 0)); // Decrement total quantity
        // If the item is already in the list, update its quantity
        return prevItems.map((item) =>
          item.ItemId === ItemId ? { ...item, Quantity: Math.max(item.Quantity - 1, 0) } : item
        );
      } else {
        // If the item is not in the list, do nothing
        return prevItems;
      }
    });
  };

  const getTotalQuantityForCategory = (categoryId) => {
    return selectedItems
      .filter((item) => item.CategoryId === categoryId)
      .reduce((total, item) => total + item.Quantity, 0);
  };

  const submitData = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const kotdata = {
      TableId: tableId,
      CreatedBy: userId,
      CompanyId: companyId,
      CreatedDate: new Date(),
    };

    const CreateData = {
      KOT: kotdata,
      KOTDetails: selectedItems
    };

    postData('KOTApi', CreateData, token)
      .then((response) => {
        console.log('KOT Created success:', response);
        setSuccessMessage("KOT Created successfully!");
        setErrorMessage("");
        setIsLoading(false);
        if (response.response === '1111' && companyId != 4) {
          const kotid = response.kotId;
          handleKOTPrint(kotid);
        }

        history.push('/Home');
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      })
      .catch((error) => {
        console.error('KOT Create error:', error);
      });

  };

  const handleKOTPrint = async (kotid) => {
    try {
      const response = await fetchData('KOTApi', `/KOTPrint/${kotid}`, token);
      const labelData = response.response_data;
      const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
      iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

      ReactDOM.render(
        <EstimatePrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
        iframeDocument.body
      );

      iframeDocument.write('</body></html>');
      iframeDocument.close();
      iframe.contentWindow.print();

      try {
        // Update each record to the API after printing
        SyncData('KOTApi', `${kotid}`, token);
      } catch (updateError) {
        console.error('Error updating record:', updateError);
      }

    } catch (error) {
      console.error('Error printing Estimate:', error);
    }
  };

  return (
    <div className="container mx-auto mt-2 p-4 max-w-screen-md" style={{ maxHeight: '820px', overflowY: 'auto' }}>
      <style>
        {`
        /* Hide scrollbar for Chrome, Safari, and Opera */
        .container::-webkit-scrollbar {
          width: 0.2em;
        }

        .container::-webkit-scrollbar-track {
          background: rgba(255,255,255,0);
        }

        .container::-webkit-scrollbar-thumb {
          background-color: rgba(255,255,255,0);
          border-radius: 4px;
        }
      `}
      </style>
      <form onSubmit={submitData}>
        {totalQuantity > 0 && (
          <div className="flex mb-2">
            <div className="banner-style" style={{ display: 'flex', alignItems: 'center', padding: '10px', borderRadius: '8px', width: '100%' }}>
              <RestaurantMenu style={{ color: 'brown' }} />
              <h6 className="flex-grow text-black font-serif" style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif', marginLeft: '10px' }}>
                <span style={{ fontFamily: 'arial', fontSize: '16px',fontWeight:'bolder' }}>{totalQuantity}</span> ITEMS
              </h6>
              <Button
                // variant="contained"
                // color="primary"
                // style={{ marginLeft: 'auto' }}
                style={{
                  backgroundColor: '#294B29',
                  borderRadius: '50px',
                  padding: '10px 20px',
                  color: 'whitesmoke',
                  fontWeight: 'bolder',
                  textTransform: 'none',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                  maxHeight: '35px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: '10px',

              }}
                onClick={submitData}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : <b>Send to Kitchen</b>}
              </Button>
            </div>
          </div>
        )}

        {isModalOpen && (
          <DescriptionModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            itemId={selectedItemId}
            description={description}
            onSave={saveDescription}
          />
        )}

        <div className="flex mb-2">
          <h5 className="flex-grow text-black font-serif" style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>
            KOT - {tableName}
          </h5>
          <div>
            <p className="text-black font-bold">{`${kotDate} - ${kotTime}`}</p>
          </div>
        </div>
        {categoryData.map((category) => (
          <Card key={category.CategoryName} sx={{ marginBottom: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.1)', backgroundColor: 'rgba(255,255,255,0.8)' }}>
            <CardContent>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  marginBottom: 1,
                  display: 'flex',
                  alignItems: 'center',
                  height: '10px',
                  fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif',
                  fontSize: '20px',
                  cursor: 'pointer', // Add cursor style for indicating it's clickable
                }}
                onClick={() => handleExpandClick(category.CategoryName)}
              >
                {category.CategoryName}
                {getTotalQuantityForCategory(category.CategoryId) > 0 && (
                  <span style={{ marginLeft: 'auto', marginRight: '10px' }}>
                    ({getTotalQuantityForCategory(category.CategoryId)} items)
                  </span>
                )}
                <IconButton
                  aria-expanded={expandedCategories.includes(category.CategoryName)}
                  aria-label="show more"
                  sx={{ marginLeft: 'auto' }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Typography>
              <Collapse in={expandedCategories.includes(category.CategoryName)} timeout="auto" unmountOnExit>
                <TableContainer>
                  <Table>
                    <TableBody>
                      {category.Items.map((item) => (
                        <TableRow key={item.Id}>
                          <TableCell sx={{ fontSize: '16px', width: '220px' }}>{item.Name}</TableCell>
                          <TableCell sx={{ width: '180px', padding: '1px', fontSize: '16px', fontWeight: 'bold' }}>
                            <IconButton
                              color="secondary"
                              style={{ marginRight: '5px' }}
                              onClick={() => handleDecrement(item.Id)}
                            >
                              <RemoveIcon />
                            </IconButton>
                            {selectedItems.find((selectedItem) => selectedItem.ItemId === item.Id)?.Quantity || 0}
                            <IconButton
                              color="primary"
                              style={{ marginLeft: '5px' }}
                              onClick={() => handleIncrement(item.CategoryId, item.Id, item.Name)}
                            >
                              <AddIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ width: '20px', padding: '1px', fontSize: '16px', fontWeight: 'bold' }}>
                            {selectedItems.find((selectedItem) => selectedItem.ItemId === item.Id)?.Quantity > 0 && (
                              <IconButton
                                color="secondary"
                                onClick={() => {
                                  setSelectedItemId(item.Id);
                                  setDescription(selectedItems.find((selectedItem) => selectedItem.ItemId === item.Id)?.description || '');
                                  setIsModalOpen(true);
                                }}
                              >
                                <InfoIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </CardContent>
          </Card>
        ))}
        <div className="flex mb-2">
          <div>
            <p className="text-white font-bold" hidden>{kotTime}</p>
          </div>
        </div>
        <div className="flex mb-2">
          <div>
            <p className="text-white font-bold" hidden>{kotDate}</p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateKOT;
